'use client'

// Import React and necessary hooks for managing state and side effects.
import React, { useState, useEffect } from 'react'
// Import CSS module styles
import styles from './TerminalTabs.module.css'
// Import navigation hooks from Next.js to work with URL and search parameters
import { usePathname, useSearchParams } from 'next/navigation'
// Import the ZerodhaConnector for fetching orders
import { ZerodhaConnector } from '../ZerodhaConnector'

/**
 * @uiComponent
 * @name OpenOrders
 * @description The OpenOrders component displays pending stock trading orders on the Zerodha platform.
 * It shows detailed information about each open order including symbol, price, quantity, status,
 * and order types. Features filtering by symbol, URL-based symbol filtering for focused trading views,
 * and comprehensive order management capabilities.
 * @location Open Orders tab in the trading terminal
 * @prop none - No external props, manages internal state and connector integration
 */

/**
 * @uiWorkflow
 * @name View and Manage Open Stock Orders on Zerodha
 * @description How to monitor and understand your pending stock trading orders
 * @platform zerodha
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Open Orders" tab [component: OpenOrders] [action: click]
 * @step 3 Review all pending orders in the table [component: OpenOrders] [action: observe]
 * @step 4 Use the symbol dropdown to filter orders by specific stocks [component: OpenOrders] [action: select]
 * @step 5 Check the "Hide other pairs" option to focus on current symbol [component: OpenOrders] [action: check] [notes: Only available when viewing a specific stock page]
 * @step 6 Review order details including prices, quantities, and status [component: OpenOrders] [action: observe]
 * @step 7 Monitor order execution status and timestamps [component: OpenOrders] [action: observe]
 * @step 8 Use order information for trading decision making [component: OpenOrders] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Filter Orders by Stock Symbol on Zerodha
 * @description How to focus on orders for specific stocks
 * @platform zerodha
 * @step 1 Access the Open Orders tab [component: OpenOrders] [action: click]
 * @step 2 Locate the symbol dropdown at the top of the orders list [component: OpenOrders] [action: observe]
 * @step 3 Click the dropdown to see all available stock symbols [component: OpenOrders] [action: click]
 * @step 4 Select a specific stock symbol to filter orders [component: OpenOrders] [action: select]
 * @step 5 View only orders related to the selected stock [component: OpenOrders] [action: observe]
 * @step 6 Use "All Symbols" option to see all orders again [component: OpenOrders] [action: select]
 * @step 7 When on a stock page, use "Hide other pairs" for focused view [component: OpenOrders] [action: check]
 * @step 8 Review filtered results for better order management [component: OpenOrders] [action: analyze]
 */

// Define the interface for a Zerodha order. This tells TypeScript what properties to expect.
interface ZerodhaOrder {
  orderId: string
  symbol: string
  quantity: number
  price: number
  triggerPrice: number
  type: string
  side: string
  status: string
  createdAt: string
  updatedAt: string
  filledQuantity: number
  averagePrice: number
  remainingQuantity: number
  product?: string
  exchange?: string
  variety?: string
  orderType?: string
}

// Define an interface to group an order with its expanded state and detail flag.
interface OrderGroup {
  mainOrder: ZerodhaOrder
  expanded: boolean
  hasDetails: boolean
}

// This is the main component for displaying open orders.
export default function OpenOrders({ apiKey, apiSecret, accessToken }: { apiKey: string, apiSecret: string, accessToken?: string }) {
  // Get URL search parameters and pathname using Next.js hooks.
  const searchParams = useSearchParams()
  const pathname = usePathname()
  // Extract the last part of the path (if available) and convert it to uppercase.
  const pathSymbol = pathname ? pathname.split('/').pop()?.toUpperCase() : ''
  // Use the symbol from the path or from the URL search parameters.
  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  // Set up state variables:
  // orders: filtered list of orders to display.
  // allOrders: full list of orders fetched from the API.
  // isLoading: flag to show loading state.
  // error: holds error message if fetching orders fails.
  // selectedSymbol: the currently selected symbol from the dropdown.
  // hideOtherPairs: flag to filter orders based on URL symbol.
  // availableSymbols: list of unique symbols available in the orders.
  // expandedOrders: an object holding the expanded state (true/false) of each order.
  const [orders, setOrders] = useState<ZerodhaOrder[]>([])
  const [allOrders, setAllOrders] = useState<ZerodhaOrder[]>([]) // Store all orders
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState('ALL')
  const [hideOtherPairs, setHideOtherPairs] = useState(false)
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([])
  const [expandedOrders, setExpandedOrders] = useState<{[key: string]: boolean}>({})

  // This useEffect runs once when the component mounts or when apiKey, apiSecret, or accessToken change.
  // It calls fetchOrders to get the latest orders.
  useEffect(() => {
    fetchOrders()
  }, [apiKey, apiSecret, accessToken])
  
  // This useEffect runs whenever selectedSymbol, hideOtherPairs, allOrders, or urlSymbol changes.
  // It filters the orders based on the current selections.
  useEffect(() => {
    if (allOrders.length > 0) {
      filterOrders()
    }
  }, [selectedSymbol, hideOtherPairs, allOrders, urlSymbol])

  // Function to fetch orders using the ZerodhaConnector.
  const fetchOrders = async () => {
    // Only proceed if both apiKey and apiSecret are provided.
    if (!apiKey || !apiSecret) return

    setIsLoading(true)
    setError(null)

    try {
      // Create an instance of ZerodhaConnector with the provided API credentials.
      const connector = new ZerodhaConnector(apiKey, apiSecret)
      
      // Call the getOpenOrders method to fetch orders.
      const data = await connector.getOpenOrders()
      
      // Check if data is an array and contains orders.
      if (Array.isArray(data) && data.length > 0) {
        // Save all orders to state.
        setAllOrders(data)
        
        // Extract unique symbols from the orders and add 'ALL' as the first option.
        const symbols = ['ALL', ...new Set(data.map((order: ZerodhaOrder) => order.symbol))]
        setAvailableSymbols(symbols as string[])
        
        // Filter orders based on the current selection and hideOtherPairs flag.
        filterOrders(data)
      } else {
        console.log('No orders data returned or empty array')
        setAllOrders([])
        setOrders([])
        setAvailableSymbols(['ALL'])
      }
    } catch (err: any) {
      // Log and set any error that occurs during the fetch.
      console.error('Error fetching orders:', err)
      setError(err.message || 'Failed to fetch orders')
    } finally {
      // Turn off the loading state.
      setIsLoading(false)
    }
  }

  // Function to filter orders based on the selected symbol and hideOtherPairs flag.
  // If ordersData is provided, use that; otherwise, use the allOrders state.
  const filterOrders = (ordersData = allOrders) => {
    let filteredOrders = ordersData

    // Apply URL symbol filter if "Hide other pairs" is checked
    if (hideOtherPairs && urlSymbol) {
      filteredOrders = filteredOrders.filter(order => order.symbol === urlSymbol)
    } else if (selectedSymbol !== 'ALL') {
      // Apply dropdown filter if not showing all symbols
      filteredOrders = filteredOrders.filter(order => order.symbol === selectedSymbol)
    }

    // Sort orders by creation date, most recent first
    filteredOrders = filteredOrders.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    setOrders(filteredOrders)
  }

  // Event handler for when the dropdown selection changes.
  const handleSymbolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSymbol(e.target.value)
  }

  // Event handler for when the "Hide other pairs" checkbox changes.
  const handleHideOtherPairsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHideOtherPairs(e.target.checked)
  }

  // Helper function to check if an order has additional details (for example, a trigger price).
  const hasDetails = (order: ZerodhaOrder): boolean => {
    return order.triggerPrice > 0 || order.status === 'TRIGGER PENDING'
  }

  // Helper function to return a description for the order type.
  // If the order has a trigger price, it distinguishes between stop loss limit and market orders.
  const getOrderTypeDescription = (order: ZerodhaOrder): string => {
    if (order.triggerPrice > 0) {
      if (order.type === 'LIMIT') {
        return 'Stop Loss Limit'
      } else {
        return 'Stop Loss Market'
      }
    }
    return order.type
  }

  // Function to toggle the expanded view of order details.
  const toggleExpanded = (orderId: string) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }))
  }

  // Helper function to format a date string for display.
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  }

  // Function to group orders along with their expanded state and detail availability.
  const getOrderGroups = (orders: ZerodhaOrder[]): OrderGroup[] => {
    return orders.map(order => ({
      mainOrder: order,
      expanded: !!expandedOrders[order.orderId],
      hasDetails: hasDetails(order)
    }))
  }

  // If orders are still loading, display a loading message.
  if (isLoading) return <div>Loading orders...</div>
  // If an error occurred, display the error message.
  if (error) return <div className={styles.error}>{error}</div>
  // We'll always show the filter controls, even if there are no orders

  // Render the orders table and filtering options.
  return (
    <div className={styles.ordersContainer}>
      {/* Section for selecting the symbol */}
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={selectedSymbol}
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
            // Disable dropdown if "Hide other pairs" is enabled and a URL symbol exists.
            disabled={hideOtherPairs && urlSymbol ? true : false}
          >
            {availableSymbols.map(symbol => (
              <option key={symbol} value={symbol}>
                {symbol === 'ALL' ? 'All Symbols' : symbol}
              </option>
            ))}
          </select>
        </div>

        {/* Show checkbox option only if a URL symbol is present */}
        {urlSymbol && (
          <div className={styles.filterOption}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hideOtherPairs}
                onChange={handleHideOtherPairsChange}
                className={styles.checkbox}
              />
              Hide other pairs
            </label>
          </div>
        )}
      </div>

      {/* Display a message if no orders match the filter, otherwise show the orders table */}
      {orders.length === 0 ? (
        <div className={styles.emptyState}>
          {hideOtherPairs && urlSymbol
            ? `No open orders found for ${urlSymbol}`
            : selectedSymbol === 'ALL'
              ? 'No open orders found'
              : `No open orders found for ${selectedSymbol}`}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                {/* Empty header for the expand/collapse button */}
                <th></th>
                <th>Created At</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {/* Map over the grouped orders to display each one */}
              {getOrderGroups(orders).map(group => {
                // Destructure the main order, its expanded state, and if it has extra details.
                const { mainOrder, expanded, hasDetails: hasOrderDetails } = group;
              
                return (
                  <React.Fragment key={mainOrder.orderId}>
                    {/* Main row showing basic order details */}
                    <tr className={hasOrderDetails ? styles.groupMainRow : ''}>
                      <td>
                        {/* If order has details, show a button to toggle the expanded view */}
                        {hasOrderDetails && (
                          <button
                            className={styles.expandButton}
                            onClick={() => toggleExpanded(mainOrder.orderId)}
                          >
                            {expanded ? '▼' : '►'}
                          </button>
                        )}
                      </td>
                      <td>{formatDate(mainOrder.createdAt)}</td>
                      <td>{mainOrder.symbol}</td>
                      <td className={mainOrder.side === 'BUY' ? styles.buyOrder : styles.sellOrder}>
                        {mainOrder.side}
                      </td>
                      <td>{mainOrder.type}</td>
                      <td>{mainOrder.quantity}</td>
                      <td>₹{mainOrder.price.toLocaleString()}</td>
                      <td>{mainOrder.status}</td>
                    </tr>

                    {/* Sub-row that displays additional order details if available and expanded */}
                    {hasOrderDetails && expanded && (
                      <tr className={styles.subRow}>
                        <td></td>
                        <td colSpan={7} className={styles.subRowInfo}>
                          <div className={styles.orderDescription}>
                            <strong>{getOrderTypeDescription(mainOrder)}:</strong> Order with trigger conditions
                          </div>
                          <div className={styles.orderDetails}>
                            {mainOrder.triggerPrice > 0 && (
                              <div>
                                <strong>Trigger Price:</strong> ₹{mainOrder.triggerPrice.toLocaleString()}
                              </div>
                            )}
                            {mainOrder.product && (
                              <div>
                                <strong>Product:</strong> {mainOrder.product}
                              </div>
                            )}
                            {mainOrder.exchange && (
                              <div>
                                <strong>Exchange:</strong> {mainOrder.exchange}
                              </div>
                            )}
                            <div>
                              <strong>Filled:</strong> {mainOrder.filledQuantity} / {mainOrder.quantity}
                            </div>
                            {mainOrder.averagePrice > 0 && (
                              <div>
                                <strong>Average Price:</strong> ₹{mainOrder.averagePrice.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
